import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lookupFidePlayer } from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import {
  computeFideAutofill,
  type FideAutofillConflict,
  type FideAutofillTarget,
} from '@/lib/fide-autofill';

type ApplyFn = (values: Partial<FideAutofillTarget>) => void;

interface Pending {
  conflicts: FideAutofillConflict[];
  conflictValues: Partial<FideAutofillTarget>;
  apply: ApplyFn;
}

// Story 098 — drives a "Look up FIDE ID" action on either surface. Applies
// empty-field fills immediately; surfaces conflicts for confirmation via the
// returned state (rendered by <FideAutofillDialog/>).
export function useFideAutofill(i18nNamespace: string) {
  const { t } = useTranslation(i18nNamespace);
  const [looking, setLooking] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  async function lookup(
    fideId: string | number,
    current: FideAutofillTarget,
    apply: ApplyFn,
  ) {
    const id = String(fideId ?? '').trim();
    if (!/^\d+$/.test(id)) {
      showErrorToast(t('fide_autofill.no_id'));
      return;
    }
    setLooking(true);
    try {
      const rec = await lookupFidePlayer(id);
      const { fills, conflicts, conflictValues } = computeFideAutofill(current, rec);
      if (Object.keys(fills).length > 0) apply(fills);
      if (conflicts.length > 0) {
        setPending({ conflicts, conflictValues, apply });
      } else {
        showSuccessToast(t('fide_autofill.filled'));
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        showErrorToast(t('fide_autofill.not_found'));
      } else {
        showErrorToast(t('fide_autofill.lookup_failed'));
      }
    } finally {
      setLooking(false);
    }
  }

  function confirmOverwrite() {
    if (!pending) return;
    pending.apply(pending.conflictValues);
    setPending(null);
    showSuccessToast(t('fide_autofill.filled'));
  }

  function dismiss() {
    setPending(null);
  }

  return {
    looking,
    lookup,
    conflicts: pending?.conflicts ?? null,
    confirmOverwrite,
    dismiss,
  };
}
